import type { FaceBox } from '@/types';
import { clamp } from '@/lib/utils';

/**
 * Face detection with two tiers.
 *
 * 1. The native `FaceDetector` API (Chromium behind a flag on some platforms).
 *    Accurate boxes, reported as source: 'native'.
 * 2. A skin-tone + connected-component heuristic that runs everywhere.
 *    It is genuinely approximate: it finds *skin-like regions*, not faces, so
 *    boxes are reported as source: 'heuristic' with reduced confidence, and
 *    the UI labels them as such. For medical/portrait work the protected area
 *    it produces is still worth honoring — over-protecting a region costs a
 *    little composition freedom, under-protecting it puts type on a face.
 */

interface NativeFaceDetector {
  detect(source: CanvasImageSource): Promise<{
    boundingBox: { x: number; y: number; width: number; height: number };
  }[]>;
}

type FaceDetectorCtor = new (opts?: {
  maxDetectedFaces?: number;
  fastMode?: boolean;
}) => NativeFaceDetector;

function nativeDetectorCtor(): FaceDetectorCtor | null {
  const ctor = (globalThis as Record<string, unknown>).FaceDetector;
  return typeof ctor === 'function' ? (ctor as FaceDetectorCtor) : null;
}

export function hasNativeFaceDetection(): boolean {
  return nativeDetectorCtor() !== null;
}

async function detectNative(
  img: HTMLImageElement,
): Promise<FaceBox[] | null> {
  const Ctor = nativeDetectorCtor();
  if (!Ctor) return null;
  try {
    const detector = new Ctor({ maxDetectedFaces: 8, fastMode: false });
    const faces = await detector.detect(img);
    return faces.map((f) => ({
      x: f.boundingBox.x / img.naturalWidth,
      y: f.boundingBox.y / img.naturalHeight,
      w: f.boundingBox.width / img.naturalWidth,
      h: f.boundingBox.height / img.naturalHeight,
      confidence: 0.95,
      source: 'native' as const,
    }));
  } catch {
    return null;
  }
}

/** YCbCr skin-tone gate — the standard chrominance window, plus an RGB check. */
function isSkin(r: number, g: number, b: number): boolean {
  const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
  const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
  const chroma = cb >= 77 && cb <= 132 && cr >= 133 && cr <= 178;
  const rgbRule =
    r > 60 &&
    g > 30 &&
    b > 15 &&
    r > g &&
    r > b &&
    Math.max(r, g, b) - Math.min(r, g, b) > 12 &&
    Math.abs(r - g) > 8;
  return chroma && rgbRule;
}

function detectHeuristic(
  data: Uint8ClampedArray,
  w: number,
  h: number,
): FaceBox[] {
  const mask = new Uint8Array(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    if (isSkin(data[i], data[i + 1], data[i + 2])) mask[p] = 1;
  }

  // Iterative flood fill (an explicit stack, so a large region cannot blow
  // the call stack the way recursion would).
  const seen = new Uint8Array(w * h);
  const boxes: FaceBox[] = [];
  const stack: number[] = [];
  const minArea = Math.max(24, Math.round(w * h * 0.004));

  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || seen[start]) continue;
    stack.length = 0;
    stack.push(start);
    seen[start] = 1;

    let minX = w;
    let maxX = 0;
    let minY = h;
    let maxY = 0;
    let count = 0;

    while (stack.length) {
      const p = stack.pop() as number;
      const px = p % w;
      const py = (p / w) | 0;
      count++;
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;

      const neighbours = [
        px > 0 ? p - 1 : -1,
        px < w - 1 ? p + 1 : -1,
        py > 0 ? p - w : -1,
        py < h - 1 ? p + w : -1,
      ];
      for (const n of neighbours) {
        if (n >= 0 && mask[n] && !seen[n]) {
          seen[n] = 1;
          stack.push(n);
        }
      }
    }

    if (count < minArea) continue;
    const bw = maxX - minX + 1;
    const bh = maxY - minY + 1;
    const aspect = bw / bh;
    const fill = count / (bw * bh);
    // Faces and heads are roughly upright and reasonably solid. This filters
    // out arms, hands and background walls in a skin-adjacent hue.
    if (aspect < 0.45 || aspect > 1.7 || fill < 0.42) continue;

    boxes.push({
      x: minX / w,
      y: minY / h,
      w: bw / w,
      h: bh / h,
      confidence: clamp(0.35 + fill * 0.3, 0.3, 0.7),
      source: 'heuristic',
    });
  }

  return boxes
    .sort((a, b) => b.w * b.h - a.w * a.h)
    .slice(0, 4);
}

/**
 * Returns face boxes normalized to the image. Prefers native detection and
 * falls back to the heuristic; returns [] when neither finds anything.
 */
export async function detectFaces(
  img: HTMLImageElement,
  sample: { data: Uint8ClampedArray; width: number; height: number },
): Promise<FaceBox[]> {
  const native = await detectNative(img);
  if (native && native.length > 0) return native;
  return detectHeuristic(sample.data, sample.width, sample.height);
}
